import type { ReactNode } from 'react';
import { CalendarDays, MessageSquareWarning, Plane, Ruler } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Reveal } from '@/components/ui/reveal';
import { EncroachmentVisual, UpdatesVisual, DistanceVisual } from './problem-visuals';

type Problem = {
  title: string;
  blurb: string;
  /** Label + icon for the inner panel's header rule. */
  panelLabel: string;
  panelIcon: ReactNode;
  visual: ReactNode;
};

const PROBLEMS: Problem[] = [
  {
    title: 'Silent encroachment',
    blurb:
      'Markers get moved a few feet at a time and fences creep over the line. By the time anyone notices, the encroacher has months of possession behind them.',
    panelLabel: 'Boundary drift',
    panelIcon: <Ruler className="size-4 shrink-0 text-ring" />,
    visual: <EncroachmentVisual />,
  },
  {
    title: 'Unreliable updates',
    blurb:
      'Relatives and watchmen report back vaguely, infrequently, or leave out what nobody wants to be blamed for. Without dated photos, you cannot check any of it.',
    panelLabel: 'Last 3 updates',
    panelIcon: <MessageSquareWarning className="size-4 shrink-0 text-ring" />,
    visual: <UpdatesVisual />,
  },
  {
    title: 'The distance tax',
    blurb:
      'A site visit means flights, time zones, and days of lost work — all to confirm nothing has changed. Almost nobody goes often enough to catch a problem while it is small.',
    panelLabel: 'Cost of one visit',
    panelIcon: <Plane className="size-4 shrink-0 text-ring" />,
    visual: <DistanceVisual />,
  },
];

/**
 * Feature-4 layout: centred section head over a 3-up grid of equal-height cards,
 * each a soft outer shell wrapping an inset panel that carries a small visual.
 * Rendered in the page's own tokens and display type rather than the block's
 * SaaS chrome — hairlines and paper, not drop shadows.
 */
export function Problems() {
  return (
    <section id="problems" className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 md:py-28">
      <Reveal className="flex flex-col items-center">
        <h2 className="max-w-3xl text-center font-display text-3xl font-medium leading-tight tracking-tight text-foreground [overflow-wrap:anywhere] md:text-4xl">
          The longer your plot sits unwatched, the more it <span className="text-ring">costs</span>{' '}
          you.
        </h2>
        <p className="mt-5 max-w-xl text-center leading-relaxed text-foreground/65">
          Remote owners are the easiest targets. Without regular on-ground visibility, problems
          surface long after the damage is done.
        </p>
      </Reveal>

      <div className="mt-12 grid items-stretch gap-6 md:grid-cols-3">
        {PROBLEMS.map(({ title, blurb, panelLabel, panelIcon, visual }, idx) => (
          <Reveal key={title} delay={idx * 0.08} className="h-full">
            <Card className="flex h-full flex-col gap-0 rounded-3xl border-border bg-secondary/40 p-0 shadow-none">
              <CardContent className="flex h-full flex-col gap-6 p-3 sm:p-4">
                <div className="px-2 pt-3 text-center">
                  <h3 className="font-display text-xl font-medium text-foreground">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/65">{blurb}</p>
                </div>

                {/* The inset panel is where the block puts its product screenshot;
                    here it holds a small diagram of the problem instead. */}
                <div className="flex min-h-[280px] flex-1 flex-col rounded-2xl border border-border bg-background p-4">
                  <div className="flex items-center justify-between gap-2 border-b border-border pb-3">
                    <span className="flex items-center gap-2 text-sm text-foreground/70">
                      {panelIcon}
                      {panelLabel}
                    </span>
                    <CalendarDays className="size-4 shrink-0 text-foreground/40" />
                  </div>
                  <div className="flex flex-1 flex-col justify-center pt-4">{visual}</div>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        ))}
      </div>

      <p className="mt-6 text-center text-sm text-foreground/60">
        Illustrative — the panels above depict the problem, not measured results.
      </p>
    </section>
  );
}
